/* ================================================================
   DashboardSidebar.tsx — VERSION 3.8
   Fix: Module Discipline autonome avec 3 sous-menus
        Retrait de /discipline du module académique
   ================================================================ */

import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import {
  LayoutDashboard, Users, BookOpen, CalendarDays,
  ShieldAlert, Wallet, Newspaper, FileSearch, Settings, X,
  ChevronRight, LogOut, School, Lock, Search, UserPlus, Upload,
  UsersRound, ArrowLeftRight, History, Ban, BookMarked, Layers,
  GraduationCap, UserCheck, MessageSquare,
  BarChart3, TrendingUp, Trophy, FileText, Crown,
  ArrowLeft, Calculator, Percent, Award, AlertTriangle, RotateCcw, ListChecks,
  FileBarChart2, Gavel, Play, Edit3,
  Banknote, Receipt, Clock, ClipboardList, Target, Sparkles
} from 'lucide-react'
import { useAuthStore } from '@/store/authStore'
import { schoolApi } from '@/services/api'
import { useLang } from '@/hooks/useLang'
import { useMemo, useCallback, useState, useEffect } from 'react'
import toast from 'react-hot-toast'

interface SidebarProps {
  onClose?: () => void
  mobile?: boolean
}

const FULL_ACCESS_ROLES = ['super_admin', 'admin', 'proviseur']

// RÈGLE MÉTIER : un enseignant n'a accès QU'AU module Évaluation (notes),
// et dans ce module UNIQUEMENT au sous-menu « Notes & Moyennes » —
// indépendamment de son jeu de permissions en base.
const TEACHER_ROLE_NAMES = ['enseignant', 'teacher', 'professeur']
const TEACHER_ALLOWED_MODULES = ['grades']
// Seul sous-menu visible pour un enseignant dans le module Évaluation
const TEACHER_GRADES_ONLY_PATH = '/grades'

const t = {
  fr: {
    schoolShort: 'Lycée Bilingue',
    schoolSub: 'de Baleng',
    dashboard: 'Tableau de bord',
    backToModules: '← Retour aux modules',
    logout: 'Déconnexion',
    logoutSuccess: 'Déconnecté',
    noAccess: 'Aucun module accessible',
    students: 'Inscription',
    timetable: 'Pédagogie',
    academics: 'Académique',
    grades: 'Évaluation',
    statistics: 'Statistiques & Rapports',
    budget: 'Budget Scolaire',
    administration: 'Administration',
    content: 'Contenu',
    audit: 'Audit',
    fees: 'Scolarité',
    feeTypes: 'Types de frais',
    feeStructures: 'Structures de frais',
    feePayments: 'Paiements',
    feeReports: 'Rapports',
    viewStudents: 'Voir les élèves',
    globalSearch: 'Recherche globale',
    createStudent: 'Créer un élève',
    importStudents: 'Importer la liste',
    bulkUpdate: 'Mise à jour groupée',
    socialCases: 'Cas sociaux',
    transferredStudents: 'Élèves transférés',
    formerStudents: 'Anciens élèves',
    expelledStudents: 'Élèves renvoyés',
    specialties: 'Spécialités & Séries',
    levelsClasses: 'Niveaux & Classes',
    assignStudent: "Mettre l'élève en classe",
    classCouncil: 'Conseil de classe (manuel)',
    classCouncilAuto: 'Conseil de classe auto',
    classCouncilExpress: 'Conseil de classe express',
    yearEndDecisions: "Décisions fin d'année",
    enrollmentFromCouncil: 'Inscription anciens',
    discipline: 'Discipline',
    disciplineStudents: 'Discipline élèves',
    disciplineTeachers: 'Assiduité enseignants',
    apcTracking: 'Suivi programmes (APC)',
    gradesLabel: 'Notes & Moyennes',
    gradesAverage: 'Moyennes',
    gradesRanking: 'Classement',
    gradesReportCards: 'Bulletins',
    gradesHonorRoll: "Tableau d'honneur",
    groupScolarite: 'Scolarité',
    groupPv: 'Procès-verbaux',
    groupResults: 'Résultats',
    statsNiveau: 'Statistiques par niveau',
    listProvisoire: 'Liste provisoire',
    listDefinitive: 'Liste définitive',
    effectifsClasses: 'Effectif des classes',
    ficheSaisieNotes: 'Fiche de saisie des notes',
    pvSequentiel: 'PV Séquentiel',
    pvTrimestrielle: 'PV Trimestrielle',
    pvAnnuelle: 'PV Annuelle',
    livretBepcDecc: 'Livret BEPC DECC',
    listMajors: 'Liste des majors',
    tauxReussite: 'Taux de réussite',
    listAdmis: 'Admis (conseil de classe)',
    listExclus: 'Exclus (conseil de classe)',
    listRedoublants: 'Redoublants (conseil de classe)',
    settings: 'Paramètres',
    users: 'Utilisateurs',
    auditLogs: 'Audit & Logs',
    publishedContent: 'Contenu publié',
    teachers: 'Enseignants',
    timetableLabel: 'Emploi du temps',
    messages: 'Communication',
    aiAssistant: 'Assistant IA',
  },
  en: {
    schoolShort: 'Bilingual High School',
    schoolSub: 'of Baleng',
    dashboard: 'Dashboard',
    backToModules: '← Back to modules',
    logout: 'Logout',
    logoutSuccess: 'Logged out',
    noAccess: 'No accessible module',
    students: 'Enrollment',
    timetable: 'Pedagogy',
    academics: 'Academic',
    grades: 'Assessment',
    statistics: 'Statistics & Reports',
    budget: 'School Budget',
    administration: 'Administration',
    content: 'Content',
    audit: 'Audit',
    fees: 'School Fees',
    feeTypes: 'Fee Types',
    feeStructures: 'Fee Structures',
    feePayments: 'Payments',
    feeReports: 'Reports',
    viewStudents: 'View Students',
    globalSearch: 'Global Search',
    createStudent: 'Create Student',
    importStudents: 'Import List',
    bulkUpdate: 'Bulk Update',
    socialCases: 'Social Cases',
    transferredStudents: 'Transferred Students',
    formerStudents: 'Former Students',
    expelledStudents: 'Expelled Students',
    specialties: 'Specialties & Series',
    levelsClasses: 'Levels & Classes',
    assignStudent: 'Assign Student to Class',
    classCouncil: 'Class Council (manual)',
    classCouncilAuto: 'Auto Class Council',
    classCouncilExpress: 'Express Class Council',
    yearEndDecisions: 'Year-End Decisions',
    enrollmentFromCouncil: 'Former Students Enrollment',
    discipline: 'Discipline',
    disciplineStudents: 'Student Discipline',
    disciplineTeachers: 'Teacher Attendance',
    apcTracking: 'Program Tracking (CBA)',
    gradesLabel: 'Grades & Averages',
    gradesAverage: 'Averages',
    gradesRanking: 'Ranking',
    gradesReportCards: 'Report Cards',
    gradesHonorRoll: 'Honor Roll',
    groupScolarite: 'Enrollment',
    groupPv: 'Transcripts',
    groupResults: 'Results',
    statsNiveau: 'Level Statistics',
    listProvisoire: 'Provisional List',
    listDefinitive: 'Definitive List',
    effectifsClasses: 'Class Enrollments',
    ficheSaisieNotes: 'Grade Entry Form',
    pvSequentiel: 'Sequential Transcript',
    pvTrimestrielle: 'Quarterly Transcript',
    pvAnnuelle: 'Annual Transcript',
    livretBepcDecc: 'BEPC DECC Booklet',
    listMajors: 'Top Students',
    tauxReussite: 'Pass Rates',
    listAdmis: 'Admitted (class council)',
    listExclus: 'Excluded (class council)',
    listRedoublants: 'Repeating (class council)',
    settings: 'Settings',
    users: 'Users',
    auditLogs: 'Audit & Logs',
    publishedContent: 'Published Content',
    teachers: 'Teachers',
    timetableLabel: 'Timetable',
    messages: 'Communication',
    aiAssistant: 'Assistant IA',
  }
}

interface MenuItem {
  path: string
  label: string
  icon: any
  perm: string | string[]
  group?: string
}

interface ModuleMenu {
  id: string
  label: string
  icon: any
  perm: string | string[]
  items: MenuItem[]
}

function buildModules(lang: 'fr' | 'en'): ModuleMenu[] {
  const txt = t[lang]
  return [
    {
      id: 'fees',
      label: txt.fees,
      icon: Banknote,
      perm: 'fees_read',
      items: [
        { path: '/fees/dashboard', label: txt.dashboard, icon: BarChart3, perm: 'fees_read' },
        { path: '/fees', label: txt.feePayments, icon: Banknote, perm: 'fees_read' },
        { path: '/fees/types', label: txt.feeTypes, icon: Layers, perm: 'fees_manage' },
        { path: '/fees/structures', label: txt.feeStructures, icon: Calculator, perm: 'fees_manage' },
        { path: '/fees/reports', label: txt.feeReports, icon: FileBarChart2, perm: 'fees_read' },
      ],
    },
    {
      id: 'students',
      label: txt.students,
      icon: Users,
      perm: 'students_read',
      items: [
        { path: '/students/dashboard', label: txt.dashboard, icon: BarChart3, perm: 'students_read' },
        { path: '/students', label: txt.viewStudents, icon: Users, perm: 'students_read' },
        { path: '/students/search', label: txt.globalSearch, icon: Search, perm: 'students_read' },
        { path: '/students/create', label: txt.createStudent, icon: UserPlus, perm: 'students_create' },
        { path: '/students/import', label: txt.importStudents, icon: Upload, perm: 'students_create' },
        { path: '/students/bulk-update', label: txt.bulkUpdate, icon: UsersRound, perm: 'students_update' },
        { path: '/students/social-cases', label: txt.socialCases, icon: BookMarked, perm: 'students_read' },
        { path: '/students/transferred', label: txt.transferredStudents, icon: ArrowLeftRight, perm: 'students_read' },
        { path: '/students/former', label: txt.formerStudents, icon: History, perm: 'students_read' },
        { path: '/students/expelled', label: txt.expelledStudents, icon: Ban, perm: 'students_read' },
      ],
    },
    {
      id: 'timetable',
      label: txt.timetable,
      icon: CalendarDays,
      perm: 'timetable_read',
      items: [
        { path: '/timetable/dashboard', label: txt.dashboard, icon: BarChart3, perm: 'timetable_read' },
        { path: '/teachers', label: txt.teachers, icon: Users, perm: 'timetable_read' },
        { path: '/timetable', label: txt.timetableLabel, icon: CalendarDays, perm: 'timetable_read' },
      ],
    },
    {
      id: 'academics',
      label: txt.academics,
      icon: GraduationCap,
      perm: ['specialties_read', 'levels_read', 'assign_student', 'class_council_read', 'class_council_write', 'year_end_decisions_read'],
      items: [
        { path: '/academics/dashboard', label: txt.dashboard, icon: BarChart3, perm: ['specialties_read', 'levels_read', 'assign_student', 'class_council_read', 'class_council_write', 'year_end_decisions_read'] },
        { path: '/academics/specialties', label: txt.specialties, icon: GraduationCap, perm: 'specialties_read' },
        { path: '/academics/levels', label: txt.levelsClasses, icon: Layers, perm: 'levels_read' },
        { path: '/academics/assign', label: txt.assignStudent, icon: UserCheck, perm: 'assign_student' },
        { path: '/academics/year-end-decisions', label: txt.yearEndDecisions, icon: Gavel, perm: 'year_end_decisions_read' },
        { path: '/academics/council-auto', label: txt.classCouncilAuto, icon: Play, perm: 'class_council_write' },
        { path: '/academics/council-express', label: txt.classCouncilExpress, icon: Edit3, perm: 'class_council_write' },
        { path: '/academics/council', label: txt.classCouncil, icon: MessageSquare, perm: 'class_council_read' },
        { path: '/academics/enrollment', label: txt.enrollmentFromCouncil, icon: UserPlus, perm: 'students_create' },
      ],
    },
    {
      id: 'discipline',
      label: txt.discipline,
      icon: ShieldAlert,
      perm: ['discipline_read', 'discipline_write', 'teacher_discipline_read', 'apc_tracking_read'],
      items: [
        { path: '/discipline/dashboard', label: txt.dashboard, icon: BarChart3, perm: ['discipline_read', 'discipline_write', 'teacher_discipline_read', 'apc_tracking_read'] },
        { path: '/discipline/students', label: txt.disciplineStudents, icon: ShieldAlert, perm: ['discipline_read', 'discipline_write'] },
        { path: '/discipline/teachers', label: txt.disciplineTeachers, icon: Clock, perm: 'teacher_discipline_read' },
        { path: '/discipline/apc', label: txt.apcTracking, icon: Target, perm: 'apc_tracking_read' },
      ],
    },
    {
      id: 'grades',
      label: txt.grades,
      icon: BookOpen,
      perm: 'grades_read',
      items: [
        { path: '/grades/dashboard', label: txt.dashboard, icon: BarChart3, perm: 'grades_read' },
        { path: '/grades', label: txt.gradesLabel, icon: BookOpen, perm: 'grades_read' },
        { path: '/grades/average', label: txt.gradesAverage, icon: TrendingUp, perm: 'grades_read' },
        { path: '/grades/ranking', label: txt.gradesRanking, icon: Trophy, perm: 'grades_read' },
        { path: '/grades/bulletins', label: txt.gradesReportCards, icon: FileText, perm: 'grades_read' },
        { path: '/grades/honor', label: txt.gradesHonorRoll, icon: Crown, perm: 'grades_read' },
      ],
    },
    {
      id: 'statistics',
      label: txt.statistics,
      icon: FileBarChart2,
      perm: ['statistics_read', 'list_provisoire_read', 'list_definitive_read', 'effectifs_read', 'saisie_notes_read', 'pv_read', 'livret_read', 'majors_read', 'taux_reussite_read', 'decisions_conseil_read'],
      items: [
        { path: '/statistics/dashboard', label: txt.dashboard, icon: BarChart3, perm: 'statistics_read' },
        { path: '/statistics/niveau', label: txt.statsNiveau, icon: BarChart3, perm: ['statistics_niveau_read', 'statistics_read'] },
        { path: '/statistics/scolarite/provisoire', label: txt.listProvisoire, icon: ListChecks, perm: 'list_provisoire_read', group: 'scolarite' },
        { path: '/statistics/scolarite/definitive', label: txt.listDefinitive, icon: ListChecks, perm: 'list_definitive_read', group: 'scolarite' },
        { path: '/statistics/scolarite/effectifs', label: txt.effectifsClasses, icon: Users, perm: 'effectifs_read', group: 'scolarite' },
        { path: '/statistics/scolarite/saisie-notes', label: txt.ficheSaisieNotes, icon: Calculator, perm: 'saisie_notes_read', group: 'scolarite' },
        { path: '/statistics/proces-verbaux/sequentiel', label: txt.pvSequentiel, icon: FileText, perm: 'pv_read', group: 'pv' },
        { path: '/statistics/proces-verbaux/trimestrielle', label: txt.pvTrimestrielle, icon: FileText, perm: 'pv_read', group: 'pv' },
        { path: '/statistics/proces-verbaux/annuelle', label: txt.pvAnnuelle, icon: FileText, perm: 'pv_read', group: 'pv' },
        { path: '/statistics/livret-bepc-decc', label: txt.livretBepcDecc, icon: BookOpen, perm: 'livret_read' },
        { path: '/statistics/majors', label: txt.listMajors, icon: Award, perm: 'majors_read', group: 'results' },
        { path: '/statistics/taux-reussite', label: txt.tauxReussite, icon: Percent, perm: 'taux_reussite_read', group: 'results' },
        { path: '/council/admitted', label: txt.listAdmis, icon: ListChecks, perm: 'decisions_conseil_read', group: 'results' },
        { path: '/council/excluded', label: txt.listExclus, icon: AlertTriangle, perm: 'decisions_conseil_read', group: 'results' },
        { path: '/council/repeating', label: txt.listRedoublants, icon: RotateCcw, perm: 'decisions_conseil_read', group: 'results' },
      ],
    },
    {
      id: 'budget',
      label: txt.budget,
      icon: Wallet,
      perm: 'budget_read',
      items: [
        { path: '/budget/dashboard', label: txt.dashboard, icon: BarChart3, perm: 'budget_read' },
        { path: '/budget', label: txt.budget, icon: Wallet, perm: 'budget_read' },
      ],
    },
    {
      id: 'administration',
      label: txt.administration,
      icon: Settings,
      perm: ['users_read', 'settings_read', 'admin_full'],
      items: [
        { path: '/administration/dashboard', label: txt.dashboard, icon: BarChart3, perm: ['users_read', 'settings_read', 'admin_full'] },
        { path: '/settings', label: txt.settings, icon: Settings, perm: 'settings_read' },
        { path: '/settings/users', label: txt.users, icon: Users, perm: 'users_read' },
      ],
    },
    {
      id: 'content',
      label: txt.content,
      icon: Newspaper,
      perm: 'news_publish',
      items: [
        { path: '/content/dashboard', label: txt.dashboard, icon: BarChart3, perm: 'news_publish' },
        { path: '/content', label: txt.publishedContent, icon: Newspaper, perm: 'news_publish' },
      ],
    },
    {
      id: 'messages',
      label: txt.messages,
      icon: MessageSquare,
      perm: ['messages_read', 'messages_manage'],
      items: [
        { path: '/messages/dashboard', label: txt.messages, icon: MessageSquare, perm: ['messages_read', 'messages_manage'] },
        { path: '/messages', label: txt.messages, icon: MessageSquare, perm: ['messages_read', 'messages_manage'] },
      ],
    },
    {
      id: 'audit',
      label: txt.audit,
      icon: FileSearch,
      perm: 'audit_read',
      items: [
        { path: '/audit/dashboard', label: txt.dashboard, icon: BarChart3, perm: 'audit_read' },
        { path: '/audit', label: txt.auditLogs, icon: FileSearch, perm: 'audit_read' },
      ],
    },
    {  
      id: 'ai',  
      label: txt.aiAssistant,  
      icon: Sparkles,  
      perm: 'audit_read',  
      items: [    
        { path: '/ai', 
          label: txt.aiAssistant, 
          icon: Sparkles, 
          perm: 'audit_read' 
        },  
      ],
    },
  ]
}

const ROUTE_TO_MODULE: Record<string, string> = {
  '/admin': 'dashboard',
  '/dashboard': 'dashboard',
  '/fees': 'fees',
  '/fees/dashboard': 'fees',
  '/fees/types': 'fees',
  '/fees/structures': 'fees',
  '/fees/reports': 'fees',
  '/students': 'students',
  '/students/dashboard': 'students',
  '/students/search': 'students',
  '/students/create': 'students',
  '/students/import': 'students',
  '/students/bulk-update': 'students',
  '/students/social-cases': 'students',
  '/students/transferred': 'students',
  '/students/former': 'students',
  '/students/expelled': 'students',
  '/timetable': 'timetable',
  '/timetable/dashboard': 'timetable',
  '/teachers': 'timetable',
  '/grades': 'grades',
  '/grades/dashboard': 'grades',
  '/grades/average': 'grades',
  '/grades/ranking': 'grades',
  '/grades/bulletins': 'grades',
  '/grades/honor': 'grades',
  // V3.8: Module Discipline autonome
  '/discipline': 'discipline',
  '/discipline/dashboard': 'discipline',
  '/discipline/students': 'discipline',
  '/discipline/teachers': 'discipline',
  '/discipline/apc': 'discipline',
  '/academics': 'academics',
  '/academics/dashboard': 'academics',
  '/academics/specialties': 'academics',
  '/academics/levels': 'academics',
  '/academics/assign': 'academics',
  '/academics/council': 'academics',
  '/academics/council-express': 'academics',
  '/academics/year-end-decisions': 'academics',
  '/academics/council-auto': 'academics',
  '/academics/enrollment': 'academics',
  '/statistics': 'statistics',
  '/statistics/dashboard': 'statistics',
  '/statistics/niveau': 'statistics',
  '/statistics/scolarite/provisoire': 'statistics',
  '/statistics/scolarite/definitive': 'statistics',
  '/statistics/scolarite/effectifs': 'statistics',
  '/statistics/scolarite/saisie-notes': 'statistics',
  '/statistics/proces-verbaux/sequentiel': 'statistics',
  '/statistics/proces-verbaux/trimestrielle': 'statistics',
  '/statistics/proces-verbaux/annuelle': 'statistics',
  '/statistics/livret-bepc-decc': 'statistics',
  '/statistics/majors': 'statistics',
  '/statistics/taux-reussite': 'statistics',
  '/statistics/admis': 'statistics',
  '/statistics/exclus': 'statistics',
  '/statistics/redoublants': 'statistics',
  '/council/admitted': 'statistics',
  '/council/excluded': 'statistics',
  '/council/repeating': 'statistics',
  '/settings': 'administration',
  '/settings/users': 'administration',
  '/administration/dashboard': 'administration',
  '/audit': 'audit',
  '/audit/dashboard': 'audit',
  '/content': 'content',
  '/content/dashboard': 'content',
  '/budget': 'budget',
  '/budget/dashboard': 'budget',
  '/messages/dashboard': 'messages',
  '/messages': 'messages',
  '/ai': 'ai',
}

const GROUP_STYLES: Record<string, { fr: string; en: string; bar: string }> = {
  scolarite: { fr: 'Scolarité', en: 'Enrollment', bar: 'border-l-2 border-l-emerald-400' },
  pv: { fr: 'Procès-verbaux', en: 'Transcripts', bar: 'border-l-2 border-l-indigo-400' },
  results: { fr: 'Résultats', en: 'Results', bar: 'border-l-2 border-l-amber-400' },
}

export default function DashboardSidebar({ onClose, mobile }: SidebarProps) {
  const location = useLocation()
  const navigate = useNavigate()
  const { user, logout, permissions, role, roleName } = useAuthStore()
  const { lang } = useLang()
  const txt = t[lang]

  const userRole = role || ''
  const hasFullAccess = FULL_ACCESS_ROLES.includes(userRole)
  const isTeacherRole = TEACHER_ROLE_NAMES.includes(userRole)

  // Nom de l'établissement depuis la DB (fallback : textes statiques)
  const [schoolInfo, setSchoolInfo] = useState<{ short: string; sub: string } | null>(null)

  useEffect(() => {
    const loadSchool = async () => {
      try {
        const res: any = await schoolApi.getSchools()
        const schools = Array.isArray(res) ? res : res?.data || []
        const userSchoolId = (user as any)?.school_id
        const s = schools.find((x: any) => x.id === userSchoolId) || schools[0]
        if (s) {
          setSchoolInfo({
            short: s.short_name || s.name || '',
            sub: s.acronym || s.code || s.city || 'My Helper School',
          })
        }
      } catch { /* fallback textes statiques */ }
    }
    loadSchool()
  }, [(user as any)?.school_id])

  const rawPerms: string[] = useMemo(() => {
    if (Array.isArray(permissions)) return permissions
    if (typeof permissions === 'object' && permissions !== null) {
      return Object.entries(permissions as Record<string, boolean>)
        .filter(([, v]) => v === true)
        .map(([k]) => k)
    }
    return []
  }, [permissions])

  const checkPermission = useCallback((perm: string | string[]): boolean => {
    if (hasFullAccess) return true
    if (!rawPerms.length) return false
    if (Array.isArray(perm)) {
      return perm.some((p) => rawPerms.includes(p))
    }
    return rawPerms.includes(perm)
  }, [rawPerms, hasFullAccess])

  const currentPath = location.pathname
  const currentModuleId = useMemo(() => {
    if (ROUTE_TO_MODULE[currentPath]) return ROUTE_TO_MODULE[currentPath]
    for (const [route, mod] of Object.entries(ROUTE_TO_MODULE)) {
      if (currentPath.startsWith(route + '/')) return mod
    }
    return 'dashboard'
  }, [currentPath])

  const isDashboardView = currentPath === '/admin' || currentPath === '/dashboard'

  const allModules = useMemo(() => buildModules(lang), [lang])

  const visibleModules = useMemo(() => {
    return allModules.filter(mod => {
      // V3.9 : enseignant → uniquement le module Évaluation
      if (isTeacherRole && !TEACHER_ALLOWED_MODULES.includes(mod.id)) return false
      return mod.items.some(item => checkPermission(item.perm))
    })
  }, [allModules, checkPermission, isTeacherRole])

  const activeModule = useMemo(() => {
    if (isDashboardView) return null
    return allModules.find(m => m.id === currentModuleId) || null
  }, [allModules, currentModuleId, isDashboardView])

  const onLogout = () => {
    logout()
    toast.success(txt.logoutSuccess)
  }

  const handleClose = () => {
    if (onClose) onClose()
  }

  if (isDashboardView) {
    return (
      <div className="flex flex-col h-full bg-school-blue text-white">
        <div className="flex items-center justify-between h-16 px-4 border-b border-white/10">
          <div className="flex items-center gap-2">
            <School className="h-7 w-7 text-school-gold" />
            <div className="overflow-hidden">
              <span className="font-bold text-sm block truncate">{schoolInfo?.short || txt.schoolShort}</span>
              <span className="block text-[10px] text-school-gold truncate">{schoolInfo?.sub || txt.schoolSub}</span>
            </div>
          </div>
          {mobile && (
            <button onClick={handleClose} className="p-1 hover:bg-white/10 rounded">
              <X className="h-5 w-5" />
            </button>
          )}
        </div>

        <div className="px-4 py-3 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-full bg-school-gold flex items-center justify-center text-school-blue font-bold text-sm">
              {(user as any)?.first_name?.[0]}{(user as any)?.last_name?.[0]}
            </div>
            <div className="overflow-hidden">
              <p className="text-sm font-medium truncate">{(user as any)?.first_name} {(user as any)?.last_name}</p>
              <p className="text-xs text-white/60 truncate">{roleName || userRole}</p>
            </div>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto py-4 px-2 space-y-1">
          <NavLink
            to="/admin"
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                isActive ? 'bg-white/20 text-white' : 'text-white/70 hover:bg-white/10 hover:text-white'
              }`
            }
          >
            <LayoutDashboard className="flex-shrink-0 h-5 w-5" />
            <span className="flex-1">{txt.dashboard}</span>
          </NavLink>

          {visibleModules.map((mod) => {
            const ModIcon = mod.icon
            const isActive = currentModuleId === mod.id
            return (
              <NavLink
                key={mod.id}
                to={isTeacherRole && mod.id === 'grades' ? TEACHER_GRADES_ONLY_PATH : (mod.items[0]?.path || '/admin')}
                onClick={() => { handleClose() }}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  isActive ? 'bg-white/20 text-white' : 'text-white/70 hover:bg-white/10 hover:text-white'
                }`}
              >
                <ModIcon className={`flex-shrink-0 h-5 w-5 ${isActive ? 'text-school-gold' : ''}`} />
                <span className="flex-1">{mod.label}</span>
                <ChevronRight className="h-4 w-4 text-white/40" />
              </NavLink>
            )
          })}

          {visibleModules.length === 0 && (
            <div className="px-3 py-4 text-center">
              <Lock className="h-8 w-8 text-white/30 mx-auto mb-2" />
              <p className="text-xs text-white/50">{txt.noAccess}</p>
              <p className="text-[10px] text-white/30 mt-1">
                role={userRole} | perms={rawPerms.length}
              </p>
            </div>
          )}
        </nav>

        <div className="p-4 border-t border-white/10">
          <button
            onClick={() => { onLogout(); handleClose() }}
            className="flex items-center gap-3 px-3 py-2 w-full rounded-lg text-sm font-medium text-white/70 hover:bg-white/10 hover:text-white transition"
          >
            <LogOut className="h-5 w-5" />
            <span>{txt.logout}</span>
          </button>
        </div>
      </div>
    )
  }

  // V3.9 : sous-menus filtrés — enseignant = uniquement « Notes & Moyennes »
  const allModuleItems = activeModule?.items.filter(item => checkPermission(item.perm)) || []
  const moduleItems = (isTeacherRole && activeModule?.id === 'grades')
    ? allModuleItems.filter(item => item.path === TEACHER_GRADES_ONLY_PATH)
    : allModuleItems

  return (
    <div className="flex flex-col h-full bg-school-blue text-white">
      <div className="flex items-center justify-between h-16 px-4 border-b border-white/10">
        <div className="flex items-center gap-2">
          <School className="h-7 w-7 text-school-gold" />
          <div className="overflow-hidden">
            <span className="font-bold text-sm block truncate">{schoolInfo?.short || txt.schoolShort}</span>
            <span className="block text-[10px] text-school-gold truncate">{schoolInfo?.sub || txt.schoolSub}</span>
          </div>
        </div>
        {mobile && (
          <button onClick={handleClose} className="p-1 hover:bg-white/10 rounded">
            <X className="h-5 w-5" />
          </button>
        )}
      </div>

      <div className="px-4 py-3 border-b border-white/10">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-full bg-school-gold flex items-center justify-center text-school-blue font-bold text-sm">
            {(user as any)?.first_name?.[0]}{(user as any)?.last_name?.[0]}
          </div>
          <div className="overflow-hidden">
            <p className="text-sm font-medium truncate">{(user as any)?.first_name} {(user as any)?.last_name}</p>
            <p className="text-xs text-white/60 truncate">{roleName || userRole}</p>
          </div>
        </div>
      </div>

      <div className="px-2 py-2 border-b border-white/10">
        <button
          onClick={() => {
            navigate('/admin')
            handleClose()
          }}
          className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-white/70 hover:bg-white/10 hover:text-white transition-all w-full text-left"
        >
          <ArrowLeft className="h-4 w-4" />
          <span>{txt.backToModules}</span>
        </button>
      </div>

      {activeModule && (
        <div className="px-4 py-3 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center">
              <activeModule.icon className="h-5 w-5 text-school-gold" />
            </div>
            <div>
              <p className="text-sm font-semibold">{activeModule.label}</p>
              <p className="text-[10px] text-white/50">
                {moduleItems.length} {lang === 'fr' ? 'menus' : 'menus'}
              </p>
            </div>
          </div>
        </div>
      )}

      <nav className="flex-1 overflow-y-auto py-2 px-2 space-y-0.5">
        {moduleItems.map((item, idx, arr) => {
          const Icon = item.icon
          const isActive = currentPath === item.path
          const prevItem = arr[idx - 1]
          const showGroupHeader = item.group && (!prevItem || prevItem.group !== item.group)
          const groupStyle = item.group ? GROUP_STYLES[item.group] : null

          return (
            <div key={item.path}>
              {showGroupHeader && groupStyle && (
                <div className={`mt-3 mb-1 ml-2 pl-3 py-1 ${groupStyle.bar}`}>
                  <span className="text-[10px] uppercase tracking-wider text-white/40 font-bold">
                    {groupStyle[lang]}
                  </span>
                </div>
              )}
              <NavLink
                to={item.path}
                onClick={() => { handleClose() }}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all ${
                  isActive
                    ? 'bg-white/20 text-white font-medium'
                    : 'text-white/60 hover:bg-white/10 hover:text-white'
                }`}
              >
                <Icon className="flex-shrink-0 h-4 w-4" />
                <span className="flex-1">{item.label}</span>
                {isActive && <ChevronRight className="h-3.5 w-3.5" />}
              </NavLink>
            </div>
          )
        })}

        {moduleItems.length === 0 && (
          <div className="px-3 py-4 text-center">
            <Lock className="h-8 w-8 text-white/30 mx-auto mb-2" />
            <p className="text-xs text-white/50">{txt.noAccess}</p>
          </div>
        )}
      </nav>

      <div className="p-4 border-t border-white/10">
        <button
          onClick={() => { onLogout(); handleClose() }}
          className="flex items-center gap-3 px-3 py-2 w-full rounded-lg text-sm font-medium text-white/70 hover:bg-white/10 hover:text-white transition"
        >
          <LogOut className="h-5 w-5" />
          <span>{txt.logout}</span>
        </button>
      </div>
    </div>
  )
}